import { Plan } from '@common/api/billingService';

export type BillingCycle = 'monthly' | 'yearly';
export type SelectablePlanCode =
    | 'SOLO_199'
    | 'SOLO_399'
    | 'BASIC'
    | 'SOLO_999'
    | 'SOLO_1299'
    | 'TEAM_1499'
    | 'TEAM_2999'
    | 'PRO'
    | 'TEAM_9999'
    | 'TEAM_49999';

export interface TeamOfferOption {
    planCode: SelectablePlanCode;
    label: string;
    monthlyAmount: number;
}

const normalizeCode = (value?: string | null): string => (value || '').trim().toUpperCase();
const normalizeCycle = (value?: string | null): BillingCycle => (value === 'yearly' ? 'yearly' : 'monthly');

const SELECTABLE_PLAN_CODES: readonly SelectablePlanCode[] = [
    'SOLO_199',
    'SOLO_399',
    'BASIC',
    'SOLO_999',
    'SOLO_1299',
    'TEAM_1499',
    'TEAM_2999',
    'PRO',
    'TEAM_9999',
    'TEAM_49999',
];

export const isSelectablePlanCode = (value?: string | null): value is SelectablePlanCode => (
    SELECTABLE_PLAN_CODES.includes(normalizeCode(value) as SelectablePlanCode)
);

const toNumberOrNull = (value: unknown): number | null => {
    if (value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const getPlanAmount = (plan?: Plan | null): number | null => {
    if (!plan) return null;

    const displayMonthly = toNumberOrNull(plan.displayMonthlyAmountUsd);
    if (displayMonthly !== null) {
        return displayMonthly;
    }

    if (normalizeCycle(plan.billingCycle) === 'yearly') {
        return null;
    }

    return toNumberOrNull(plan.chargeAmountUsd) ?? toNumberOrNull(plan.priceUsd);
};

const getPlanVariant = (plan: Plan): string => {
    const rawVariant = plan.rules && typeof plan.rules === 'object' ? plan.rules.variant : null;
    return normalizeCode(typeof rawVariant === 'string' ? rawVariant : null);
};

export const isTeamPlanCode = (planCode?: string | null): boolean => {
    const normalizedCode = normalizeCode(planCode);
    return normalizedCode === 'TEAM_1499'
        || normalizedCode === 'TEAM_2999'
        || normalizedCode === 'PRO'
        || normalizedCode === 'TEAM_9999'
        || normalizedCode === 'TEAM_49999';
};

export const isSoloPlanCode = (planCode?: string | null): boolean => {
    const normalizedCode = normalizeCode(planCode);
    return normalizedCode === 'SOLO_199'
        || normalizedCode === 'SOLO_399'
        || normalizedCode === 'BASIC'
        || normalizedCode === 'SOLO_999'
        || normalizedCode === 'SOLO_1299';
};

const isTeamOfferPlan = (plan: Plan): boolean => {
    const normalizedCode = normalizeCode(plan.code);
    const variant = getPlanVariant(plan);
    return isTeamPlanCode(normalizedCode) || isTeamPlanCode(variant);
};

export const formatTeamOfferLabel = (planCode: SelectablePlanCode): string => (
    planCode === 'TEAM_1499'
        ? '10 users'
        : planCode === 'TEAM_2999'
            ? '30 users'
            : planCode === 'TEAM_9999'
                ? '100 users'
                : planCode === 'TEAM_49999'
                    ? 'Unlimited users'
                    : '50 users'
);

export const buildTeamOfferOptions = (
    plans: Plan[],
    cycle: BillingCycle,
): TeamOfferOption[] => {
    const activeTeamPlans = plans.filter((plan) => plan.isActive && isTeamOfferPlan(plan));
    const seenCodes = new Set<string>();

    return activeTeamPlans
        .filter((plan) => normalizeCycle(plan.billingCycle) === cycle)
        .reduce<TeamOfferOption[]>((options, plan) => {
            const normalizedCode = normalizeCode(plan.code);
            if (!isSelectablePlanCode(normalizedCode) || seenCodes.has(normalizedCode)) {
                return options;
            }

            const monthlyAmount = getPlanAmount(plan);
            if (monthlyAmount === null) {
                return options;
            }

            seenCodes.add(normalizedCode);
            options.push({
                planCode: normalizedCode,
                label: formatTeamOfferLabel(normalizedCode),
                monthlyAmount,
            });
            return options;
        }, [])
        .sort((left, right) => left.monthlyAmount - right.monthlyAmount);
};

export const resolveSelectedTeamOfferCode = (
    options: TeamOfferOption[],
    currentPlanCode?: string | null,
    previousSelection?: string | null,
): SelectablePlanCode => {
    const currentCode = normalizeCode(currentPlanCode);
    const previousCode = normalizeCode(previousSelection);

    const previousOption = options.find((option) => option.planCode === previousCode);
    if (previousOption) return previousOption.planCode;

    const currentOption = options.find((option) => option.planCode === currentCode);
    if (currentOption) return currentOption.planCode;

    return options.reduce<TeamOfferOption | null>((highest, option) => {
        if (!highest || option.monthlyAmount > highest.monthlyAmount) {
            return option;
        }
        return highest;
    }, null)?.planCode ?? 'PRO';
};
